import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCartIcon,
  TrashIcon,
  MinusIcon,
  PlusIcon,
  ArrowLeftIcon,
  GiftIcon
} from '@heroicons/react/24/outline';
import Topbar from '../../PulseSurvey/Topbar';
import { getLocalStorageItem, successToast, errorToast } from '../../../utils/helper';
import { CompanyApi } from '../../../api/companyApi';
import cartService from './cartService';
import ConfirmPopup from '../../../component/common/modals/ConfirmPopup';

/**
 * Cart Component
 * Shopping cart page for marketplace
 * @returns {JSX.Element} Cart component
 */
const Cart = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderLoading, setOrderLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [showClearCartConfirm, setShowClearCartConfirm] = useState(false);

  // Get user data from localStorage
  const userData = getLocalStorageItem('userData')
    ? JSON.parse(getLocalStorageItem('userData'))
    : null;
  const slug = userData?.slug || 'b2b';

  /**
   * Load cart items on component mount
   */
  useEffect(() => {
    loadCartItems();
  }, []);

  const loadCartItems = () => {
    setLoading(true);
    const items = cartService.getCartItems();
    setCartItems(items);
    setLoading(false);
  };

  const fetchInfo = () => {
    console.log('fetchInfo function in Cart page');
  };

  /**
   * Handle quantity change
   */
  const handleQuantityChange = (cartItemKey, newQuantity) => {
    const result = cartService.updateCartItemQuantity(cartItemKey, newQuantity);
    if (result.success) {
      setCartItems(result.cartItems);
    }
  };

  /**
   * Handle remove item from cart
   */
  const handleRemoveItem = (cartItemKey) => {
    const result = cartService.removeFromCart(cartItemKey);
    if (result.success) {
      setCartItems(result.cartItems);
    }
  };

  /**
   * Handle clear cart
   */
  const handleClearCart = () => {
    setShowClearCartConfirm(true);
  };

  /**
   * Confirm clear cart
   */
  const confirmClearCart = () => {
    const result = cartService.clearCart();
    if (result.success) {
      setCartItems([]);
      successToast('Cart cleared successfully');
    } else {
      errorToast('Failed to clear cart');
    }
    setShowClearCartConfirm(false);
  };

  /**
   * Handle proceed to checkout
   */
  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      errorToast('Your cart is empty');
      return;
    }

    try {
      setOrderLoading(true);

      // Generate order reference using timestamp and random string
      const orderReference = `ORD-${Date.now()}-${Math.random()
        .toString(36)
        .substring(2, 9)
        .toUpperCase()}`;

      // Transform cart items to API format
      const items = cartItems.map((item) => ({
        code: item.productCode,
        productName: item.productName,
        quantity: item.quantity,
        provider: item.provider || 'default',
        price: parseFloat(item.selectedDenomination)
      }));

      const orderData = {
        orderReference,
        items
      };

      console.log('Sending order:', orderData);

      const response = await CompanyApi.sendCompanyVoucher(orderData);

      if (response?.data?.meta?.code === 1) {
        successToast('Order placed successfully!');
        cartService.clearCart();
        navigate(`/${slug}/marketplace/orders`);
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to place order. Please try again.');
      }
    } catch (error) {
      console.error('Error placing order:', error);
      errorToast(
        error?.response?.data?.meta?.message ||
          error?.message ||
          'Failed to place order. Please try again.'
      );
    } finally {
      setOrderLoading(false);
    }
  };

  /**
   * Calculate total items
   */
  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <>
      <div className="relative lg:mb-[45px]">
        <Helmet>
          <meta charSet="utf-8" />
          <title>Shopping Cart | Shoorah Admin</title>
        </Helmet>
        <Topbar title={'Shopping Cart'} infoBtn={false} setOpen={setOpen} fetchInfo={fetchInfo} />

        {/* Back Button */}
        <div className="mt-6">
          <button
            onClick={() => navigate(`/${slug}/marketplace`)}
            className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Marketplace
          </button>
        </div>

        {/* Cart Content */}
        <div className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <svg
                  className="animate-spin h-12 w-12 mx-auto text-blue-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Loading cart...</p>
              </div>
            </div>
          ) : cartItems.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCartIcon className="mx-auto h-16 w-16 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                Your cart is empty
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Start shopping to add items to your cart
              </p>
              <button
                onClick={() => navigate(`/${slug}/marketplace`)}
                className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Cart Items */}
              <div className="lg:col-span-2">
                <div className="bg-white dark:bg-shoorah-darkBgTabColor shadow rounded-lg overflow-hidden">
                  {/* Cart Header */}
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-shoorah-darkBorder flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Cart Items ({cartItems.length})
                    </h2>
                    {cartItems.length > 0 && (
                      <button
                        onClick={handleClearCart}
                        className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors duration-200"
                      >
                        Clear Cart
                      </button>
                    )}
                  </div>

                  {/* Cart Items List */}
                  <div className="divide-y divide-gray-200 dark:divide-shoorah-darkBorder">
                    {cartItems.map((item) => (
                      <div key={item.cartItemKey} className="px-6 py-4">
                        <div className="flex items-start gap-4">
                          {/* Product Image */}
                          <div className="flex-shrink-0 w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                            {item.imageUrl ? (
                              <img
                                src={item.imageUrl}
                                alt={item.productName}
                                className="w-full h-full object-contain p-1"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className="w-full h-full flex items-center justify-center"
                              style={{ display: item.imageUrl ? 'none' : 'flex' }}
                            >
                              <GiftIcon className="h-8 w-8 text-gray-400" />
                            </div>
                          </div>

                          {/* Product Details */}
                          <div className="flex-1 min-w-0">
                            {item.brandName && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">
                                {item.brandName}
                              </p>
                            )}
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                              {item.productName}
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                              Code: {item.productCode}
                            </p>

                            {/* Selected Denomination */}
                            {item.selectedDenomination && (
                              <div className="mb-3">
                                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">
                                  Selected value:
                                </p>
                                <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 border-2 border-green-200 dark:border-green-700">
                                  {item.currencyCode} {item.selectedDenomination}
                                </span>
                              </div>
                            )}

                            {/* Quantity Controls */}
                            <div className="flex items-center gap-3 mt-3">
                              <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg">
                                <button
                                  onClick={() =>
                                    handleQuantityChange(item.cartItemKey, item.quantity - 1)
                                  }
                                  className="px-2 py-1 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-l-lg transition-colors duration-200"
                                >
                                  <MinusIcon className="h-4 w-4" />
                                </button>
                                <input
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) => {
                                    const value = parseInt(e.target.value, 10);
                                    if (!isNaN(value) && value >= 1) {
                                      handleQuantityChange(item.cartItemKey, value);
                                    }
                                  }}
                                  className="w-12 text-center border-x border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none py-1"
                                />
                                <button
                                  onClick={() =>
                                    handleQuantityChange(item.cartItemKey, item.quantity + 1)
                                  }
                                  className="px-2 py-1 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-r-lg transition-colors duration-200"
                                >
                                  <PlusIcon className="h-4 w-4" />
                                </button>
                              </div>

                              <button
                                onClick={() => handleRemoveItem(item.cartItemKey)}
                                className="inline-flex items-center text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors duration-200"
                              >
                                <TrashIcon className="h-4 w-4 mr-1" />
                                Remove
                              </button>
                            </div>
                          </div>

                          {/* Discount Badge */}
                          {item.customerDiscountPercentage > 0 && (
                            <div className="flex-shrink-0">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                {item.customerDiscountPercentage}% OFF
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-shoorah-darkBgTabColor shadow rounded-lg overflow-hidden sticky top-6">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-shoorah-darkBorder">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Order Summary
                    </h2>
                  </div>

                  <div className="px-6 py-4 space-y-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Products:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {cartItems.length}
                      </span>
                    </div>
                  </div>

                  <div className="px-6 py-4 border-t border-gray-200 dark:border-shoorah-darkBorder">
                    <button
                      onClick={handleCheckout}
                      disabled={orderLoading || cartItems.length === 0}
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
                    >
                      {orderLoading ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Processing Order...
                        </>
                      ) : (
                        'Place Order'
                      )}
                    </button>
                    <button
                      onClick={() => navigate(`/${slug}/marketplace`)}
                      className="w-full mt-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium py-3 px-4 rounded-lg transition-colors duration-200"
                    >
                      Go Back to Marketplace
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Clear Cart Confirmation Dialog */}
      <ConfirmPopup
        open={showClearCartConfirm}
        setOpen={setShowClearCartConfirm}
        setAccepted={confirmClearCart}
        message="Are you sure you want to clear your cart? This action cannot be undone."
        handleNo={() => setShowClearCartConfirm(false)}
      />
    </>
  );
};

export default Cart;
